import { useState } from 'react'

import {
  Box,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext
} from '@factorialco/deprecated-design-system'
import { F0Alert, F0Select } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'

import { track } from 'modules/core/lib/Tracking'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useCreateBudget } from 'modules/finance/hooks/useBudgets'
import { events } from 'modules/finance/lib/events'
import {
  BudgetAllocationTypes,
  WizardData,
  allocationOptions
} from 'modules/finance/lib/newBudgetWizard'

import BudgetExceededOptionSelector, {
  BudgetExceededAmounts,
  BudgetExceededOptions
} from './BudgetExceededOptionSelector'

const SpendersStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { createBudget } = useCreateBudget()
  const [budgetExceededAmounts, setBudgetExceededAmounts] = useState<BudgetExceededAmounts>()

  const handleSubmit = async (formData: WizardData) => {
    const budgetAmount = Number(formData.amountCents)
    const allocatedAmount = Number(formData.allocatedAmountCents)
    // For manual selection, we can validate count. For 'all' selection, backend handles validation
    const spenderCount =
      formData.spenderSelection.type === 'manual' ? formData.spenderSelection.ids.length : 0

    if (!formData.legalEntityId || !formData.periodicity) {
      return
    }
    if (!formData.allocatedAmountCents && formData.allocationType === 'equal') {
      onDataChanged({
        ...data,
        errors: {
          allocatedAmountCents: i18n.t('budgets.forms.errors.mandatory')
        }
      })
      return Promise.reject()
    }
    if (formData.allocatedAmountCents) {
      if (!isNumber(formData.allocatedAmountCents) || allocatedAmount < 0) {
        onDataChanged({
          ...data,
          errors: {
            allocatedAmountCents: i18n.t('budgets.forms.errors.invalid_amount')
          }
        })
        return Promise.reject()
      }
      if (spenderCount) {
        const totalAllocatedAmount = allocatedAmount * spenderCount

        if (totalAllocatedAmount > budgetAmount) {
          const maxAllocationAmount = Math.floor(budgetAmount / spenderCount)

          setBudgetExceededAmounts({
            budgetAmount,
            allocatedAmount,
            totalAllocatedAmount,
            maxAllocationAmount
          })
          onDataChanged({
            ...data,
            errors: {
              allocatedAmountCents: i18n.t('budgets.forms.errors.allocation_exceeded')
            }
          })
          return Promise.reject()
        }
      }
    }

    const response = await createBudget({
      name: formData.name,
      description: formData.description,
      legalEntityId: formData.legalEntityId,
      amountCents: toCents(budgetAmount),
      effectiveFrom: formData.effectiveFrom,
      effectiveUntil: formData.effectiveUntil,
      periodicity: formData.periodicity,
      ownerEmployeeIds: formData.ownerEmployeeIds,
      viewerEmployeeIds: formData.viewerEmployeeIds,
      spenderEmployeeIds: [],
      spenderEmployeeSelection: formData.spenderSelection,
      ...(formData.allocatedAmountCents && {
        allocatedAmountCents: toCents(allocatedAmount)
      }),
      budgetableBy: formData.budgetableBy
    })
    const result = handleResult(response, (formErrors) => {
      onDataChanged({
        ...formData,
        success: false,
        errors: formErrors
      })
    })
    if (result.success) {
      track(events.completeAddBudget, { budgetId: result.data.budget?.id })
      onDataChanged({
        ...formData,
        success: true,
        budgetId: result.data.budget?.id
      })
    }
    return response
  }

  function handleBudgetExceededOption(option: BudgetExceededOptions) {
    if (!budgetExceededAmounts) return
    switch (option) {
      case 'increaseBudget':
        return onDataChanged({
          ...data,
          amountCents: budgetExceededAmounts.totalAllocatedAmount.toString(),
          allocatedAmountCents: budgetExceededAmounts.allocatedAmount.toString(),
          errors: {}
        })
      case 'changeAllocation':
        return onDataChanged({
          ...data,
          amountCents: budgetExceededAmounts.budgetAmount.toString(),
          allocatedAmountCents: budgetExceededAmounts.maxAllocationAmount.toString(),
          errors: {}
        })
    }
  }
  useOnNext(handleSubmit)

  // Convert allocationOptions to F0Select format
  const allocationSelectOptions = allocationOptions().map((option) => ({
    value: option.value,
    label: option.label
  }))

  return (
    <Box gap="s40" paddingRight="s40">
      <Box gap="s24" width="full">
        <Box gap="s8">
          <Text.Header2>{i18n.t('budgets.forms.new_budget.spenders.title')}</Text.Header2>

          <Text.Normal>{i18n.t('budgets.forms.new_budget.spenders.description')}</Text.Normal>
        </Box>
      </Box>
      <Box gap="s16" width="fit-content">
        <Box flexDirection="row" gap="s16">
          <Box style={{ minWidth: '200px' }}>
            <F0Select
              required
              label={i18n.t('budgets.forms.new_budget.spenders.allocations.selector')}
              options={allocationSelectOptions}
              value={data.allocationType}
              error={data.errors?.allocationType}
              onChange={(value: string) =>
                onDataChanged({
                  ...data,
                  // eslint-disable-next-line no-type-assertion/no-type-assertion
                  allocationType: value as BudgetAllocationTypes
                })
              }
              size="md"
            />
          </Box>
          {data.allocationType === 'equal' && (
            <Box style={{ minWidth: '200px' }}>
              <NumberInput
                required
                label={i18n.t('budgets.forms.new_budget.spenders.allocations.amount')}
                locale={i18n.locale}
                units={data.currency}
                value={data.allocatedAmountCents ? Number(data.allocatedAmountCents) : null}
                error={data.errors?.allocatedAmountCents}
                onChange={(value) =>
                  onDataChanged({
                    ...data,
                    allocatedAmountCents: value !== null ? String(value) : ''
                  })
                }
                maxDecimals={2}
                size="md"
              />
            </Box>
          )}
        </Box>
        {data.allocationType === 'equal' && !budgetExceededAmounts && (
          <F0Alert
            variant="info"
            title=""
            description={i18n.t('budgets.forms.new_budget.spenders.allocations.hint')}
          />
        )}
      </Box>
      {budgetExceededAmounts && (
        <Box gap="s16">
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('budgets.budget_exceeded_option_selector.prompt')}
          </Text.Normal>
          <BudgetExceededOptionSelector
            currency={data.currency}
            amounts={budgetExceededAmounts}
            onSelect={(option) => {
              handleBudgetExceededOption(option)
            }}
          />
        </Box>
      )}
      <Box gap="s40" maxWidth="s600">
        <Box gap="s8">
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('budgets.forms.new_budget.spenders.employees.title')}
          </Text.Normal>
          <Text.Normal>
            {i18n.t('budgets.forms.new_budget.spenders.employees.description')}
          </Text.Normal>
        </Box>
        <Box flexDirection="column">
          <EmployeeSelectorV2
            multiple
            onChange={(selection) => onDataChanged({ ...data, spenderSelection: selection })}
            label={i18n.t('budgets.forms.new_budget.spenders.employees.label')}
            messages={{
              placeholder: i18n.t('budgets.forms.new_budget.spenders.employees.placeholder')
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export const spendersStep = (): WizardStep<WizardData> => ({
  title: i18n.t('budgets.forms.new_budget.spenders.title'),
  nextText: i18n.t('common.submit'),
  render: (props) => <SpendersStep {...props} />,
  isCompleted: () => true
})
